import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { EPButton } from '@/components';
import { ResponsiveProvider, useResponsive } from '@/hooks/useResponsive';
import { StyledDrawer, StyledModal, getDrawerStyles } from './styles';

interface ResponsiveModalProps {
  visible: boolean;
  onClose: () => void;
  onOpen?: () => void;
  onSave?: () => Promise<void> | void;
  title: string;
  saveButtonText?: string;
  disableSaveButton?: boolean;
}

const ResponsiveModal = ({ onSave, ...props }: PropsWithChildren<ResponsiveModalProps>) => {
  const { token } = theme.useToken();
  const { isXl } = useResponsive();
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    await onSave?.();
    setIsSaving(false);
  }, [onSave]);

  const Modal = useCallback(({visible, onClose, title, saveButtonText, children, disableSaveButton, handleSave, isSaving}: PropsWithChildren<ResponsiveModalProps & { handleSave: () => void , isSaving: boolean }>) => {
    return (
      <StyledModal
        title={title}
        open={visible}
        onCancel={onClose}
        destroyOnHidden={true}
        footer={[
          <EPButton key="save" type="primary" onClick={handleSave} loading={isSaving} disabled={disableSaveButton}>
            {saveButtonText || t('Save changes')}
          </EPButton>,
        ]}
      >
        {children}
      </StyledModal>
    );
  }, []);

  const Drawer = useCallback(({visible, onClose, title, saveButtonText, children, disableSaveButton, handleSave, isSaving}: PropsWithChildren<ResponsiveModalProps & { handleSave: () => void , isSaving: boolean }>) => {
    return (
      <StyledDrawer
        title={title}
        placement="bottom"
        onClose={onClose}
        open={visible}
        height="auto"
        closable={true}
        styles={getDrawerStyles(token)}
      >
        {children}
        <EPButton key="save" type="primary" onClick={handleSave} className='save-button' loading={isSaving} disabled={disableSaveButton}>
          {saveButtonText || t('Save changes')}
        </EPButton>
      </StyledDrawer>
    );
  }, []);

  const Wrapper = useMemo(() => isXl ? Modal : Drawer, [isXl]);

  return (
      <Wrapper {...props} handleSave={handleSave} isSaving={isSaving} />
  )
};

export default (props: PropsWithChildren<ResponsiveModalProps>) => {
    return (
      <ResponsiveProvider>
        <ResponsiveModal {...props} />
      </ResponsiveProvider>
    );
  };